import { create } from 'zustand';
import { INotification } from './interface';

interface NotificationStore {
  notifications: INotification[];
  addNotification: (notification: Omit<INotification, 'timestamp'>) => string; // exclude both id and timestamp
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  getNotificationById: (id: string) => INotification | undefined;
}

const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],

  addNotification: (notificationData) => {
    const notification: INotification = {
      ...notificationData,
      id: notificationData?.id || `notif_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      timestamp: Date.now(),
    };

    set((state) => ({
      notifications: [...state.notifications.filter((existingNotification) => existingNotification.id !== notification.id), notification],
    }));

    return notification.id;
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id),
    }));
  },

  clearAllNotifications: () => {
    set({ notifications: [] });
  },

  getNotificationById: (id) => {
    return get().notifications.find((notification) => notification.id === id);
  },
}));

// Helper functions for easy usage
export const addNotification = (notification: Omit<INotification, 'timestamp'>) => {
  return useNotificationStore.getState().addNotification(notification);
};

export const removeNotification = (id: string) => {
  useNotificationStore.getState().removeNotification(id);
};

export const clearAllNotifications = () => {
  useNotificationStore.getState().clearAllNotifications();
};

export const useNotifications = () => {
  return useNotificationStore((state) => state.notifications);
};

export const useNotificationActions = () => {
  return useNotificationStore((state) => ({
    addNotification: state.addNotification,
    removeNotification: state.removeNotification,
    clearAllNotifications: state.clearAllNotifications,
  }));
};

export default useNotificationStore;
